import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RmsModule } from '@rms';
import { CustomerCrmService, RmsMessageService, RmsPulldownService } from '@services';
import { AddressFormComponent } from './address-form/address-form.component';

type AddressType = 'Shipping' | 'Billing' | 'Sample';

@Component({
  selector: 'app-customer-addresses',
  imports: [RmsModule],
  templateUrl: './customer-addresses.component.html',
  styleUrl: './customer-addresses.component.scss'
})
export class CustomerAddressesComponent implements OnChanges {
  @Input() companyId!: number;
  @Input() canEdit = false;
  CustomerDetails: any = null;
  readonly Types: AddressType[] = ['Shipping', 'Billing'];

  ShippingAddresses: any[] = [];
  BillingAddresses: any[] = [];
  SampleAddresses: any[] = [];

  SelectedShippingID: number | null = null;
  SelectedBillingID: number | null = null;
  SelectedSampleID: number | null = null;

  constructor(
    private crm: CustomerCrmService,
    private message: RmsMessageService,
    private pulldown: RmsPulldownService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['companyId']) {
      this.load();
    }
  }

  load() {
    if (!this.companyId) return;
    this.crm.getCustomerDetails(this.companyId).subscribe({
      next: (details) => this.CustomerDetails = details,
      error: (err) => this.message.error('Failed to load customer details: ' + this.errorText(err))
    });
    this.crm.getShipToAddresses(this.companyId).subscribe({
      next: (addresses) => {
        const active = (addresses || []).filter((a: any) => a.IsActive);
        this.ShippingAddresses = active.filter((a: any) => a.AddressType === 'Shipping');
        this.BillingAddresses = active.filter((a: any) => a.AddressType === 'Billing');
        this.SampleAddresses = active.filter((a: any) => a.AddressType === 'Sample');
        this.SelectedShippingID = this.defaultId(this.ShippingAddresses);
        this.SelectedBillingID = this.defaultId(this.BillingAddresses);
        this.SelectedSampleID = this.defaultId(this.SampleAddresses);
      },
      error: (err) => this.message.error('Failed to load addresses: ' + this.errorText(err))
    });
  }

  private defaultId(list: any[]): number | null {
    if (!list.length) return null;
    const def = list.find(a => a.IsDefault);
    return (def || list[0]).CustomerShipToAddressID;
  }

  listFor(type: AddressType): any[] {
    return type === 'Shipping' ? this.ShippingAddresses : type === 'Billing' ? this.BillingAddresses : this.SampleAddresses;
  }

  selectedIdFor(type: AddressType): number | null {
    return type === 'Shipping' ? this.SelectedShippingID : type === 'Billing' ? this.SelectedBillingID : this.SelectedSampleID;
  }

  setSelectedId(type: AddressType, id: number | string) {

    const numId = Number(id);
    if (type === 'Shipping') this.SelectedShippingID = numId;
    else if (type === 'Billing') this.SelectedBillingID = numId;
    else this.SelectedSampleID = numId;
  }

  setPrimary(type: AddressType, checked: boolean) {
    if (!this.canEdit) return;
    if (!checked) return;

    const addr = this.selectedAddress(type);
    if (!addr || addr.IsDefault) return;
    this.crm.saveShipToAddress({
      ...addr,
      AddressType: type,
      IsDefault: true,
      IsActive: true,
      CompanyID: this.companyId, 
      ModifiedBy: localStorage.getItem('GUID')
    }).subscribe({
      next: () => {
        this.message.success(`${type} primary address updated`);
        this.load();
      },
      error: (err) => this.message.error('Failed to update primary address: ' + this.errorText(err))
    });
  }

  selectedAddress(type: AddressType): any {
    const id = this.selectedIdFor(type);
    return this.listFor(type).find(a => a.CustomerShipToAddressID === id) || null;
  }

  optionLabel(addr: any): string {
    // Backfilled legacy addresses all share the generic name 'Billing'/'Shipping'/'Sample' -
    // show the actual street too so multiple entries of the same type stay distinguishable.
    const genericNames = ['Billing', 'Shipping', 'Sample'];
    const streetPart = [addr.Street, addr.City].filter(Boolean).join(', ');
    if (addr.AddressName && !genericNames.includes(addr.AddressName)) {
      return streetPart ? `${addr.AddressName} — ${streetPart}` : addr.AddressName;
    }
    return streetPart || addr.AddressName || 'Address';
  }

  addAddress(type: AddressType) {
    if (!this.canEdit) return;
    this.openForm(type);
  }

  editAddress(type: AddressType) {
    if (!this.canEdit) return;
    const current = this.selectedAddress(type);
    if (!current) return;
    this.openForm(type, current);
  }

  deleteAddress(type: AddressType) {
    if (!this.canEdit) return;
    const current = this.selectedAddress(type);
    if (!current) return;
    this.crm.deactivateShipToAddress(current.CustomerShipToAddressID, localStorage.getItem('GUID')).subscribe({
      next: () => {
        this.message.success(`${type} address deleted successfully`);
        this.load();
      },
      error: (err) => this.message.error('Failed to delete address: ' + this.errorText(err))
    });
  }

  private openForm(type: AddressType, address?: any) {
    const ref = this.pulldown.open(AddressFormComponent, {
      title: address ? `Edit ${type} Address` : `Add ${type} Address`,
      width: 'clamp(480px, 50vw, 720px)',
      data: address ? { ...address, CompanyID:this.companyId, AddressType: type } : { CompanyID: this.companyId, AddressType: type, IsActive: true, IsDefault: false }
    });

    ref.afterClosed().subscribe((result: any) => {
      if (!result || result.cancelled) return;
      result.CompanyID = this.companyId;
      result.AddressType = type;
      result.ModifiedBy = localStorage.getItem('GUID');
      this.crm.saveShipToAddress(result).subscribe({
        next: () => {
          this.message.success(`${type} address ${address ? 'updated' : 'added'} successfully`);
          this.load();
        },
        error: (err) => this.message.error('Failed to save address: ' + this.errorText(err))
      });
    });
  }

  private errorText(err: any): string {
    return err?.error?.error?.message || err?.message || 'Unknown error';
  }
}
